import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { DEFAULT_TIME_LIMIT_MS } from "@binogi-livequiz/shared";
import type {
  BinogQuestion,
  LanguageMode,
  NameMode,
  ServerMessage,
  SessionJoinedMessage,
} from "@binogi-livequiz/shared";
import { CharacterImage } from "../components/CharacterImage";
import { HostErrorBanner } from "../components/HostErrorBanner";
import { ScreenHeader } from "../components/layout/ScreenHeader";
import { Button } from "../components/ui/Button";
import { useHostAttach } from "../hooks/useHostAttach";
import { serverErrorKey } from "../lib/i18n";
import { loadHostSessionCode } from "../lib/session-storage";
import { useWebSocket } from "../lib/ws-context";

interface LobbyState {
  sessionCode: string;
  joinUrl: string;
  hostToken?: string;
  questions: BinogQuestion[];
  timeLimitMs: number;
  // Unset until SESSION_JOINED when the lobby opened without route state.
  nameMode?: NameMode;
  expectedStudents?: number;
  languageMode?: LanguageMode;
  availableLanguages?: string[];
}

/**
 * The lobby's route state for the session a SESSION_JOINED describes, for
 * a lobby opened without HostReview's: `from` keeps the questions and time
 * limit of the screen that had them.
 */
export function lobbyStateFromJoined(
  joined: SessionJoinedMessage,
  from?: { questions: BinogQuestion[]; timeLimitMs: number } | null,
): LobbyState {
  return {
    sessionCode: joined.sessionCode,
    joinUrl: `${window.location.origin}/join/${joined.sessionCode}`,
    questions: from?.questions ?? [],
    timeLimitMs: from?.timeLimitMs ?? DEFAULT_TIME_LIMIT_MS,
    nameMode: joined.nameMode,
    expectedStudents: joined.expectedStudents,
    languageMode: joined.languageMode,
    availableLanguages: joined.availableLanguages,
  };
}

export default function HostLobby() {
  const navigate = useNavigate();
  const location = useLocation();
  const ws = useWebSocket();
  const { t } = useTranslation();
  // No route state: open the session this tab hosts, like the HostEntry
  // deep link; the attach's SESSION_JOINED fills in the rest. See "Where a
  // host screen gets its session" in docs/architecture/frontend-patterns.md.
  const routeState = location.state as LobbyState | null;
  const state = useMemo<LobbyState | null>(() => {
    if (routeState) return routeState;
    const code = loadHostSessionCode();
    return code
      ? {
          sessionCode: code,
          joinUrl: `${window.location.origin}/join/${code}`,
          questions: [],
          timeLimitMs: DEFAULT_TIME_LIMIT_MS,
        }
      : null;
  }, [routeState]);

  // Names of the students in the room, for the roster: the names the
  // attach's catch-up lists, then each PLAYER_JOINED / PLAYER_LEFT. The
  // count comes from the server's playerCount, which can run ahead of the
  // names a sampled broadcast delivered.
  const [players, setPlayers] = useState<string[]>([]);
  const [playerCount, setPlayerCount] = useState(0);
  const [attached, setAttached] = useState(false);
  const [copied, setCopied] = useState(false);
  // A failed HOST_ATTACH used to be invisible — the lobby just sat on
  // "Connecting…" forever while students joined a session whose teacher
  // never appeared. Now we surface server ERROR frames immediately and
  // fall back to a timeout banner if no SESSION_JOINED arrives in 5s.
  // Holds an i18n key, translated at render.
  const [attachError, setAttachError] = useState<string | null>(null);

  const publicBase = useMemo(() => {
    try {
      const u = new URL(state?.joinUrl ?? window.location.href);
      return `${u.protocol}//${u.host}`;
    } catch {
      return window.location.origin;
    }
  }, [state?.joinUrl]);

  const fullJoinUrl =
    state?.joinUrl ?? `${publicBase}/join/${state?.sessionCode ?? ""}`;

  // Attach on mount, and again on every new socket after a drop. The
  // hostToken comes from navigation state (fresh create flow) or falls
  // back to sessionStorage (same-tab refresh of the lobby).
  const noToken = useHostAttach(state?.sessionCode, {
    hostToken: state?.hostToken,
    attachOnMount: true,
  });
  useEffect(() => {
    // No token — bounce back to /host. Sending HOST_ATTACH without one
    // would fail server-side anyway.
    if (noToken) navigate("/host");
  }, [noToken, navigate]);

  // A dropped socket is not attached until the re-attach is acknowledged:
  // show "Connecting…" again and re-arm the watchdog below. The names seen
  // so far go too: a student who left while the socket was down sent a
  // PLAYER_LEFT we never got, and the re-attach's SESSION_JOINED brings
  // the true count but no names.
  useEffect(() => {
    if (ws.status === "open") return;
    setAttached(false);
    setPlayers([]);
  }, [ws.status]);

  // Handle inbound messages
  useEffect(() => {
    const m = ws.lastMessage;
    if (!m) return;
    handle(m);
    ws.consume();
    function handle(msg: ServerMessage) {
      switch (msg.type) {
        case "SESSION_JOINED":
          // A late reply for the previous session on this socket (see
          // attachOnMount) says nothing about this lobby.
          if (msg.sessionCode !== state?.sessionCode) break;
          setAttached(true);
          setAttachError(null);
          setPlayerCount(msg.playerCount);
          // Opened without route state: put the session's settings where
          // HostReview would have, so the class size shows and the next
          // reload has them.
          if (!routeState && (!msg.host || msg.host.phase === "lobby")) {
            navigate("/host/lobby", {
              replace: true,
              state: lobbyStateFromJoined(msg, state),
            });
          }
          if (msg.host?.phase === "lobby") {
            setPlayers(msg.host.players);
          } else if (
            state &&
            (msg.host?.phase === "question" || msg.host?.phase === "ended")
          ) {
            // The quiz is already running: the teacher link opened on a
            // second device, or this lobby reloaded mid-quiz.
            navigate("/host/question", {
              state: {
                sessionCode: state.sessionCode,
                questions: state.questions,
                timeLimitMs: state.timeLimitMs,
                languageMode: state.languageMode ?? msg.languageMode,
                availableLanguages:
                  state.availableLanguages ?? msg.availableLanguages,
                catchUp: msg.host,
                catchUpAt: Date.now(),
                playerCount: msg.playerCount,
              },
            });
          } else if (msg.host?.phase === "finished") {
            navigate("/host/results", {
              state: {
                finalLeaderboard: msg.host.finished.finalLeaderboard,
                availableLanguages:
                  state?.availableLanguages ?? msg.availableLanguages,
              },
            });
          }
          break;
        case "PLAYER_JOINED":
          setPlayers((p) => (p.includes(msg.displayName) ? p : [...p, msg.displayName]));
          setPlayerCount(msg.playerCount);
          break;
        case "PLAYER_LEFT":
          setPlayers((p) => p.filter((n) => n !== msg.displayName));
          setPlayerCount(msg.playerCount);
          break;
        case "ERROR":
          setAttachError(
            serverErrorKey(msg, {
              overrides: { NOT_TEACHER: "host.errors.taken_over" },
              fallback: "host.errors.attach_failed",
            }),
          );
          break;
        default:
          break;
      }
    }
  }, [ws, state, routeState, navigate]);

  // Watchdog: if SESSION_JOINED never lands the lobby would otherwise
  // sit forever with "Connecting…". 5s is generous; the typical attach
  // completes well under 1s.
  useEffect(() => {
    if (attached || attachError) return;
    const timer = setTimeout(() => {
      setAttachError("host.errors.unreachable");
    }, 5000);
    return () => clearTimeout(timer);
  }, [attached, attachError]);

  // Never below the roster we can see, in case an older count lands after
  // a newer PLAYER_JOINED.
  const joinedCount = Math.max(playerCount, players.length);

  const handleStart = useCallback(() => {
    if (!state) return;
    ws.send({ type: "START_QUESTION", questionIndex: 0 });
    navigate("/host/question", {
      state: {
        sessionCode: state.sessionCode,
        questions: state.questions,
        timeLimitMs: state.timeLimitMs,
        languageMode: state.languageMode,
        availableLanguages: state.availableLanguages,
        playerCount: joinedCount,
      },
    });
  }, [navigate, state, ws, joinedCount]);

  if (!state) {
    return (
      <main className="min-h-full flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-white/70">{t("host.no_session")}</p>
          <Button
            onClick={() => navigate("/host")}
            className="mt-4 bg-brand-teal hover:brightness-110"
          >
            {t("host.back_to_review")}
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-full px-4 md:px-6 py-6 md:py-10 max-w-5xl mx-auto">
      <ScreenHeader
        available={state.availableLanguages}
        trailing={
          <div className="text-sm text-white/60">
            {attached ? t("host.lobby.connected") : t("host.lobby.connecting")}
          </div>
        }
      />

      {attachError && <HostErrorBanner message={t(attachError)} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="flex flex-col items-center text-center">
          <div className="text-white/70 mb-2">
            <Trans
              i18nKey="host.lobby.go_to"
              components={{
                strong: <span className="font-mono font-semibold" />,
              }}
            />
          </div>
          <div className="text-7xl md:text-8xl font-extrabold tracking-widest tabular-nums">
            {state.sessionCode}
          </div>

          <div className="mt-6 text-white/60 text-sm">
            {t("host.lobby.or_qr")}
          </div>
          <div className="mt-3 bg-white p-4 rounded-2xl">
            <QRCodeCanvas value={fullJoinUrl} size={192} level="M" />
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(fullJoinUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              } catch {
                // Older browsers / insecure contexts: fall back to a
                // hidden textarea + execCommand so a teacher on http://
                // local IP isn't left without the affordance.
                const ta = document.createElement("textarea");
                ta.value = fullJoinUrl;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              }
            }}
            className="mt-3 text-sm text-white/70 hover:text-white inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-ink2/40 ring-1 ring-white/10 hover:ring-white/20"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            {copied
              ? t("host.lobby.copy_link_copied")
              : t("host.lobby.copy_link")}
          </button>
        </section>

        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xl font-bold">
              {state.expectedStudents
                ? t("host.lobby.students_joined_of", {
                    joined: joinedCount,
                    expected: state.expectedStudents,
                  })
                : t("host.lobby.students_joined", { count: joinedCount })}
            </h2>
          </div>

          {state.expectedStudents ? (
            <div className="mb-3 h-2.5 rounded-full bg-brand-ink2 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  joinedCount >= state.expectedStudents
                    ? "bg-brand-green"
                    : "bg-brand-teal"
                }`}
                style={{
                  width: `${Math.max(
                    3,
                    Math.min(
                      100,
                      Math.round(
                        (joinedCount / state.expectedStudents) * 100,
                      ),
                    ),
                  )}%`,
                }}
              />
            </div>
          ) : null}

          <div className="bg-brand-ink2 rounded-2xl p-3 min-h-[12rem] max-h-[28rem] overflow-y-auto">
            {joinedCount === 0 ? (
              <p className="text-white/40 italic text-center py-12">
                {t("host.lobby.waiting")}
              </p>
            ) : (
              <ul className="space-y-2">
                {players.map((name) => (
                  <li
                    key={name}
                    className="flex items-center gap-3 px-3 py-2 bg-brand-ink rounded-xl animate-pop"
                  >
                    <CharacterImage
                      name={name}
                      size={48}
                      className="flex-shrink-0"
                    />
                    <span className="font-semibold text-lg truncate">
                      {name}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Button
            size="lg"
            block
            onClick={handleStart}
            disabled={!attached || joinedCount === 0}
            className="mt-6 font-bold"
          >
            {t("host.lobby.start_game")}
          </Button>
        </section>
      </div>
    </main>
  );
}
